import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Interface for BakingAndCooling cycle data
export interface BakingAndCoolingCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  bakingTime: string;
  lineNumber: string;
  bakingTempZone1: string;
  bakingTempZone2: string;
  bakingTempZone3: string;
  bakingTempZone4: string;
  productTempAfterBaking: string;
  ap: string;
  std: string;
  ad: string;
  ovenFireStartTime: string;
  ovenStopTime: string;
  productInTime: string;
  productOutTime: string;
  coolingTime: string;
  coolingTemp: string;
  coreTemp: string;
  productColor: string;
}

// Interface for offline file data
export interface OfflineFileData {
  id: string;
  cycleNum: number;
  productName?: string;
  fileData: string; // Base64 encoded file data
  fileName: string;
  fileSize: number;
  fileType: string;
  timestamp: number;
  qualityTourId: string;
  previewUrl: string; // Object URL for preview
}

// Interface for offline saved data
interface OfflineSavedData {
  cycleNo: number;
  records: BakingAndCoolingCycleData[];
  timestamp: number;
}

// Interface for the state
interface BakingAndCoolingState {
  // Fetched cycles from API
  fetchedCycles: BakingAndCoolingCycleData[];
  
  // Offline saved data
  offlineSavedData: OfflineSavedData[];
  
  // Offline files
  offlineFiles: OfflineFileData[];
  
  // Loading states
  isLoading: boolean;
  isSyncing: boolean;
  
  // Error state
  error: string | null;
  
  // Last fetch timestamp
  lastFetchTimestamp: number | null;
}

// Initial state
const initialState: BakingAndCoolingState = {
  fetchedCycles: [],
  offlineSavedData: [],
  offlineFiles: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

// Create the slice
const bakingAndCoolingSlice = createSlice({
  name: 'bakingAndCooling',
  initialState,
  reducers: {
    // Add offline saved data
    addOfflineData: (state, action: PayloadAction<OfflineSavedData>) => {
      state.offlineSavedData.push(action.payload);
    },
    
    // Clear all offline data (for cancel operation)
    clearOfflineData: (state) => {
      // Revoke all object URLs to free memory
      state.offlineFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      state.offlineSavedData = [];
      state.offlineFiles = [];
    },
    
    // Remove specific offline data item
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        item => item.cycleNo !== action.payload
      );
    },
    
    // Set fetched cycles
    setFetchedCycles: (state, action: PayloadAction<BakingAndCoolingCycleData[]>) => {
      state.fetchedCycles = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset state
    resetState: (state) => {
      // Revoke all object URLs to free memory
      state.offlineFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.offlineFiles = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set syncing state
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },

    // Add offline file
    addOfflineFile: (state, action: PayloadAction<OfflineFileData>) => {
      state.offlineFiles.push(action.payload);
    },

    // Remove offline file
    removeFile: (state, action: PayloadAction<string>) => {
      const fileIndex = state.offlineFiles.findIndex(file => file.id === action.payload);
      if (fileIndex !== -1) {
        const file = state.offlineFiles[fileIndex];
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
        state.offlineFiles.splice(fileIndex, 1);
      }
    },

    // Clear all offline files
    clearOfflineFiles: (state) => {
      state.offlineFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      state.offlineFiles = [];
    },
  },
});

// Export actions
export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
  addOfflineFile,
  removeFile,
  clearOfflineFiles,
} = bakingAndCoolingSlice.actions;

// Export selectors
export const selectBakingAndCoolingState = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling;
export const selectOfflineDataCount = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling.offlineSavedData.length;
export const selectOfflineFilesCount = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling.offlineFiles.length;
export const selectFetchedCycles = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling.fetchedCycles;
export const selectIsLoading = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling.isLoading;
export const selectIsSyncing = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling.isSyncing;
export const selectError = (state: { bakingAndCooling: BakingAndCoolingState }) => state.bakingAndCooling.error;

// Export reducer
export default bakingAndCoolingSlice.reducer;
